import { useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { RotateCcw, PauseCircle } from 'lucide-react';

import { organizationDataAtom } from '@/store';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { AdPreviewExtensions } from '@/components/campaign/ads/ad-preview-extensions';
import type { Ad, CampaignData, Keyword } from '@/lib/types';
import { effectiveText } from '@/lib/ad-copy-tokens';

interface AdPreviewModalProps {
  ad: Ad;
  campaign: CampaignData;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Google SERP-style preview of an ad (#5806). Extracted from the ad page so the
 * campaign sidebar's eye icon can open the preview in place, without navigating
 * to the ad route. Self-contained: owns the desktop/mobile toggle and the
 * regenerate (re-sample) key.
 */
export function AdPreviewModal({ ad, campaign, open, onOpenChange }: AdPreviewModalProps) {
  const organizationData = useAtomValue(organizationDataAtom);
  const [adPreviewKey, setAdPreviewKey] = useState(0);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');

  // Ad preview — pinned headlines are fixed to their slot (#5355); unpinned headlines
  // fill the remaining slots at random. Each entry carries its pin position so the
  // preview can show a 1/2/3 indicator.
  const adPreview = useMemo(() => {
    const allHeadlines = ad.headlines ?? [];
    if (!allHeadlines.length) {
      return { headlines: [] as { text: string; pin: number | null }[], descriptions: [] as string[] };
    }

    const slotCount = previewMode === 'desktop' ? 3 : 2;
    const visible = Math.min(allHeadlines.length, slotCount);
    const slots: ({ text: string; pin: number | null } | null)[] = Array.from({ length: visible }, () => null);

    // 1) Place each pinned headline in its fixed slot, when that slot is visible.
    allHeadlines.forEach((h) => {
      const pin = h.pin_position ?? null;
      if (pin && pin >= 1 && pin <= visible && slots[pin - 1] === null) {
        slots[pin - 1] = { text: h.text || '', pin };
      }
    });

    // 2) Fill the remaining slots with a random order of the unpinned headlines.
    const unpinned = allHeadlines.filter((h) => !h.pin_position);
    const order: number[] = [];
    while (order.length < unpinned.length) {
      const idx = Math.floor(Math.random() * unpinned.length);
      if (!order.includes(idx)) order.push(idx);
    }
    let next = 0;
    for (let i = 0; i < visible; i++) {
      if (slots[i] === null && next < order.length) {
        const h = unpinned[order[next++]];
        slots[i] = { text: h.text || '', pin: null };
      }
    }

    const headlines = slots.filter((s): s is { text: string; pin: number | null } => s !== null);

    const descriptionCount = Math.min(ad.descriptions?.length || 0, previewMode === 'desktop' ? 2 : 1);
    const descriptionIndices: number[] = [];
    while (
      descriptionIndices.length < descriptionCount &&
      descriptionIndices.length < (ad.descriptions?.length || 0)
    ) {
      const idx = Math.floor(Math.random() * (ad.descriptions?.length || 0));
      if (!descriptionIndices.includes(idx)) descriptionIndices.push(idx);
    }

    return {
      headlines,
      descriptions: descriptionIndices.map((idx) => ad.descriptions?.[idx]?.text || ''),
    };
  }, [ad.id, ad.headlines, ad.descriptions, adPreviewKey, previewMode]);

  const adPermutations = useMemo(() => {
    if (!ad.headlines?.length || !ad.descriptions?.length) {
      return 0;
    }

    const h = ad.headlines.length;
    const d = ad.descriptions.length;

    const factorial = (n: number): number => {
      if (n <= 1) return 1;
      return n * factorial(n - 1);
    };

    const permutation = (n: number, r: number): number => {
      if (n < r) return 0;
      return factorial(n) / factorial(n - r);
    };

    const headlinePerms = permutation(h, Math.min(h, 3));
    const descriptionPerms = permutation(d, Math.min(d, 2));

    return headlinePerms * descriptionPerms;
  }, [ad.headlines?.length, ad.descriptions?.length]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto p-0 gap-0" aria-describedby={undefined}>
        <DialogTitle className="sr-only">Ad Preview</DialogTitle>

        <div className="flex items-center justify-end gap-2 px-6 py-4 pr-14">
          <div className="flex items-center bg-gray-100 rounded-lg p-1 h-9">
            <button
              onClick={() => setPreviewMode('desktop')}
              className={`px-3 h-7 text-sm font-medium rounded-md transition-colors ${
                previewMode === 'desktop'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Desktop
            </button>
            <button
              onClick={() => setPreviewMode('mobile')}
              className={`px-3 h-7 text-sm font-medium rounded-md transition-colors ${
                previewMode === 'mobile'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Mobile
            </button>
          </div>
          <Button
            variant="outline"
            className="h-9"
            onClick={() => setAdPreviewKey(prev => prev + 1)}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Regenerate
          </Button>
        </div>

        <div className="px-6 pb-6">
          <div className="flex gap-8">
            <div className="w-56 flex-shrink-0">
              {ad.is_paused && (
                <div className="flex items-center text-amber-600 mb-3">
                  <PauseCircle className="h-4 w-4 mr-1.5" />
                  <span className="text-sm font-medium">This ad is currently paused</span>
                </div>
              )}
              <h3 className="text-lg font-semibold text-gray-900 mb-3">This ad will show for:</h3>
              <ul className="space-y-1.5">
                {ad.keywords?.map((keyword: Keyword, idx: number) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                    <span className="w-1.5 h-1.5 rounded-full border border-gray-400 flex-shrink-0" />
                    <span className="truncate">
                      {keyword.text.charAt(0).toUpperCase() + keyword.text.slice(1)}
                    </span>
                  </li>
                ))}
                {(!ad.keywords || ad.keywords.length === 0) && (
                  <li className="text-sm text-gray-400 italic">No keywords</li>
                )}
              </ul>
            </div>

            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Sample Ad:</h3>

              <div className={`bg-white border border-gray-200 rounded-lg overflow-hidden ${previewMode === 'mobile' ? 'max-w-[414px] mx-auto' : ''}`} style={{ fontFamily: 'Arial, sans-serif' }}>
                {/* Google search bar */}
                <div className="px-5 pt-4 pb-3 border-b border-gray-100">
                  <div className="flex items-center border border-gray-300 rounded-full px-4 py-2 shadow-sm">
                    <svg className="w-4 h-4 text-gray-400 mr-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
                    <span className="text-sm text-gray-700 truncate">{ad.keywords?.[0]?.text || 'search query'}</span>
                  </div>
                </div>

                <div className="px-5 py-4 space-y-6">
                  {/* Sponsored ad result */}
                  <div>
                    <div className="flex items-center gap-1 mb-0.5">
                      <span className="text-xs font-bold" style={{ color: '#202124' }}>Sponsored</span>
                    </div>
                    <div className="flex items-center gap-2 mb-0.5">
                      {organizationData?.primary_logo_url ? (
                        <img
                          src={organizationData.primary_logo_url}
                          alt=""
                          className="w-6 h-6 rounded-full object-cover border border-gray-200"
                        />
                      ) : (
                        <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center border border-gray-200">
                          <span className="text-[10px] font-bold text-gray-500">{ad.url?.replace(/^https?:\/\//, '').split('/')[0]?.charAt(0)?.toUpperCase() || 'A'}</span>
                        </div>
                      )}
                      <div className="text-sm leading-tight">
                        <div style={{ color: '#202124' }}>{ad.url?.replace(/^https?:\/\//, '').split('/')[0]}</div>
                        <div className="text-xs" style={{ color: '#4d5156' }}>{ad.url?.replace(/^https?:\/\//, '')}</div>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="flex-1 min-w-0">
                        <h2 className="text-xl leading-snug mb-0.5" style={{ color: '#1a0dab' }}>
                          {/* #5523: no pin badge in the preview — a real Google ad shows no
                              marker; pinning only fixes which headline fills which slot. */}
                          {adPreview.headlines.map((h, i) => (
                            <span key={i}>
                              {i > 0 && <span style={{ color: '#5f6368' }}> | </span>}
                              <span className="hover:underline cursor-pointer">{effectiveText(h.text)}</span>
                            </span>
                          ))}
                        </h2>
                        <p className="text-sm leading-relaxed" style={{ color: '#4d5156' }}>
                          {adPreview.descriptions.map(effectiveText).join(' ')}
                        </p>
                        <AdPreviewExtensions campaign={campaign} previewMode={previewMode} />
                      </div>
                      {campaign.image_extensions?.[0]?.signed_url && (
                        <div className="flex-shrink-0">
                          <img
                            src={campaign.image_extensions[0].signed_url}
                            alt=""
                            className={`${previewMode === 'desktop' ? 'w-28 h-28' : 'w-20 h-20'} rounded-lg object-cover border border-gray-200`}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* #5526: one organic-result placeholder for search context (was two,
                      which made the modal too tall). */}
                  <div className="border-t border-gray-100 pt-4 opacity-50">
                    <div className="flex items-center gap-2 mb-0.5">
                      <div className="w-6 h-6 bg-gray-100 rounded-full border border-gray-200" />
                      <div className="text-sm leading-tight">
                        <div className="w-32 h-3 bg-gray-200 rounded" />
                        <div className="w-48 h-2.5 bg-gray-100 rounded mt-1" />
                      </div>
                    </div>
                    <div className="w-3/4 h-4 bg-gray-100 rounded mt-2" />
                    <div className="w-full h-3 bg-gray-50 rounded mt-1.5" />
                    <div className="w-5/6 h-3 bg-gray-50 rounded mt-1" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 pb-6">
          <div className="text-sm text-gray-500 text-right">
            {adPermutations.toLocaleString()} possible ad combinations
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
